'use client';

import Image from 'next/image';
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'

interface HomeOurOfficeProps {
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	image?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title?: string
					text?: string
				}
			}[]
		}
	}[]
}

export const fields = [
	'heading.title',
	'text.text',

	'image.alt',
	'image.image_file.image.id',
	'image.image_file.image.filename_download',

	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
]

export default function HomeOurOffice({ data }: { data: HomeOurOfficeProps }) {
	return (
		<section className='HomeOurOffice relative bg-[#394448] md:bg-[#5c6356] w-full py-0 md:py-16 overflow-hidden'>
			<div className='flex flex-col md:flex-row items-start justify-end relative'>
				{/* Left column with image */}
				<div className='md:absolute left-0 h-full w-full md:w-[80%] md:rounded-lg overflow-hidden'>
					<div className='relative w-full h-[400px] md:h-full'>
						<Image
							src={data?.image?.image_file?.image?.id ? getReadableImageUrl(data.image.image_file.image.id, data.image.image_file.image.filename_download) : ''}
							alt={data?.image?.alt || ''}
							className='object-cover object-left md:rounded-lg w-full h-full'
							fill
							priority
							sizes='(max-width: 768px) 100vw, 60vw'
							quality={90}
						/>
					</div>
				</div>

				{/* Right column with content */}
				<div className='relative w-full md:w-[40%] bg-neutral-50 md:rounded-lg flex flex-col gap-12 md:my-20 pt-16 md:pt-20 p-8 2xl:p-24 md:p-14 md:pr-3 -mt-[60px] max-md:rounded-tr-[50px]'>
					{/* H Logo - positioned as overlay between sections */}
					<div className='absolute top-[-30px] md:top-1/2 lg:top-1/3 top-0 lg:right-auto lg:left-[-70px] left-35 md:transform md:-translate-y-1/2 md:-translate-x-1/2 lg:translate-x-0 z-10'>
						<Image
							src='/images/logo_h.svg'
							alt='Harmon Logo'
							width={112}
							height={112}
							className='w-16 md:w-28 h-16 md:h-28'
							priority
						/>
					</div>

					{/* Heading */}
					<div className='flex flex-col items-start gap-6'>
						<h2 className='text-4xl md:text-5xl font-normal leading-[56px] text-black'>
							{data?.heading?.title || ''}
						</h2>
						<div className='w-24 h-0.5 bg-[#403836]'></div>
					</div>

					{/* Main text */}
					{data?.text?.text && (
						<div className='mb-5 prose' dangerouslySetInnerHTML={sanitizeHTML(data.text.text)} />
					)}

					{/* Office locations */}
					{data?.content_collection?.map((office, index) => (
						<div key={office.id || index} className='flex flex-col items-start gap-2'>
							<h3 className='text-[#403836] text-xl md:text-2xl font-bold uppercase leading-loose tracking-wide'>
								{office.site_content_collections_id?.collection?.[0]?.item?.title || ''}
							</h3>
							<div className='text-[#403836] text-xl font-normal leading-7' dangerouslySetInnerHTML={sanitizeHTML(office.site_content_collections_id?.collection?.[1]?.item?.text || '')} />
						</div>
					))}
				</div>
			</div>
		</section>
	)
}